export const SERVICE_BASE = ""; // All internal APIs start with this prefix

//
// Values that can be passed as options to this service
//
export type ServiceOptions<T> = {
  //
  // If true, errors are caught and returned in the IFetchResult.error member. Otherwise, errors are
  // propagated outwards.
  //
  captureErrors?: boolean;
  //
  // If true, all responses are returned via the IFetchResult.status and IFetchResult.data members regardless
  // of response status value. Otherwise, response status values outside to 200 to 299 range will be treated
  // as errors.
  //
  captureStatus?: boolean;
  //
  // Delays the loading notification by the specified number of milliseconds. This can prevent short requests
  // from flashing a spinner.If specified az zero, no delay is added. Defaults to DEFAULT_LOADING_DELAY.
  //
  loadingDelay?: number;
  //
  // Aborts the request after the specified number of milliseconds have passed. Defaults to no timeout.
  //
  timeout?: number;
  //
  // Callback to update results as they come in; normally used to set caller state.
  //
  setResult?: (results: FetchResult<T>) => void;
  //
  // Callback to inform the caller of a change in loading status; generally not used in conjunction
  // with setResult.
  //
  onLoading?: (loading: boolean) => void; // TODO: implement this
};

const DEFAULT_LOADING_DELAY = 500;

//
// The status information from the response. It is pretty much the response minus the body properties.
//
export type ResponseStatus = {
  type: string;
  url: string;
  redirected: boolean;
  status: number;
  ok: boolean;
  statusText: string;
  bodyUsed: boolean;
};

//
// The result of the fetch operation.
//
export type FetchResult<T> = {
  loading: boolean;
  status?: ResponseStatus;
  data?: T;
  error?: Error;
};

/**
 * Copies only the status-oriented fields of a response (avoiding the body stream and such).
 * @param response the response containing the status to be copied
 */
const copyStatus = ({
  type,
  url,
  redirected,
  status,
  ok,
  statusText,
  bodyUsed,
}: ResponseStatus): ResponseStatus => ({
  type,
  url,
  redirected,
  status,
  ok,
  statusText,
  bodyUsed,
});

export async function doFetch<T>(
  url: string,
  serviceOptions?: ServiceOptions<T>,
  fetchOptions?: any // there does not appear to be @type definitions for the fetch API available
): Promise<FetchResult<T>> {
  // tslint:disable-next-line
  const setResult = serviceOptions?.setResult ?? (() => {});

  //
  // Keeps track of whether the request has been aborted
  //
  const abortController = new AbortController();
  const signal = abortController.signal;

  let delayLoadingTick: number | undefined;
  let timeoutTick: number | undefined;

  const loadingDelay = serviceOptions?.loadingDelay ?? DEFAULT_LOADING_DELAY;

  //
  // Let the caller know we're loading, after a delay if specified
  //
  if (loadingDelay === 0) {
    setResult({ loading: true });
  } else {
    delayLoadingTick = window.setTimeout(() => {
      delayLoadingTick = undefined;
      setResult({ loading: true });
    }, loadingDelay);
  }

  let result: FetchResult<T> = {
    loading: false,
  };

  try {
    //
    // If a timeout is specified, set up a timer that aborts the request if it expires before
    // being cleared
    //
    if (serviceOptions?.timeout) {
      timeoutTick = window.setTimeout(() => {
        abortController.abort();
      }, serviceOptions.timeout);
    }

    //
    // Make the fetch request and wait until it's done
    //
    const response = await fetch(`${SERVICE_BASE}${url}`, {
      signal,
      ...fetchOptions,
    });

    //
    // Clear any active timers
    //
    if (timeoutTick) window.clearTimeout(timeoutTick);
    if (delayLoadingTick) window.clearTimeout(delayLoadingTick);

    //
    // If the response status is in the error range, and we're not capturing it, throw it as an error
    //
    if (!serviceOptions?.captureStatus && !response.ok) {
      throw new Error(`Response status ${response.status} ${response.statusText}`);
    }

    //
    // Get the response body as JSON
    //
    const json = response.ok ? await response.json() : {};
    if (!signal.aborted) {
      result = {
        loading: false,
        data: json,
        status: copyStatus(response),
      };
    }
  } catch (e) {
    // cancel any timeout
    if (timeoutTick) {
      window.clearTimeout(timeoutTick);
    }
    if (delayLoadingTick) {
      window.clearTimeout(delayLoadingTick);
    }
    //
    // If enabled, capture the error and return it as the result, otherwise propagate it outward
    //
    result = { loading: false, error: e as any };
    if (!serviceOptions?.captureErrors) {
      throw e;
    }
  } finally {
    setResult(result);
  }

  return result;
}
